import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Home, ChevronDown, ChevronLeft, Search, Menu, X, ArrowRight,
  AlignJustify, Lock, Settings, Wrench, MonitorSmartphone, Settings2, MessageCircle,
  ShieldCheck, Database, Camera, Cloud, Wifi, Plane, Globe, LayoutGrid, Key,
  Monitor, Box, Activity, BarChart3, Headphones, LifeBuoy, Workflow, Share2,
  BookOpen, FileText, Library,
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import RegionSelector from './RegionSelector';
import ZheltaLogo from './ZheltaLogo';
import { useI18n } from '../context/I18nContext';
import { SYSTEMS_MENU } from '../data/systemsNav';
import { trackNavClick } from '../utils/trackNavClick';

const ICONS = {
  AlignJustify, Lock, Settings, Wrench, MonitorSmartphone, Settings2, MessageCircle,
  ShieldCheck, Database, Camera, Cloud, Wifi, Plane, Globe, LayoutGrid, Key,
  Monitor, Box, Activity, BarChart3, Headphones, LifeBuoy, Workflow, Share2,
  BookOpen, FileText, Library,
};

/* Lead-icon picker for the featured visual on the left of the panel.
   Each menu type gets a distinct, brand-consistent icon. */
const LEAD_ICON = {
  solutions: 'ShieldCheck',
  products: 'LayoutGrid',
  pricing: 'Settings',
  resources: 'BookOpen',
};

/* Helper: a link that auto-renders as an external `<a>` for absolute URLs
 * (e.g. the Plutio scheduler) and a React-Router `<Link>` for internal
 * routes. Without this, mega-menu items pointing at https://portal… would
 * be prefixed with the current origin and 404 because <Link> only handles
 * relative paths. */
function SmartLink({ to, onClick, className, children, testId }) {
  const isExternal = typeof to === 'string' && /^https?:\/\//i.test(to);
  if (isExternal) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        onClick={onClick}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

/* Apple/cart-central-style mega panel: featured visual on the left, plain text-link
   columns on the right, divider footer with "Explore all" + "Contact a specialist". */
function MegaMenu({ menuKey, menu, onNavigate }) {
  const groups = menu.groups || [];
  const f = menu.featured || {};
  const ping = (slug, label) => trackNavClick({ slug: `systems:${menuKey}:${slug}`, label });
  const LeadIcon = ICONS[LEAD_ICON[menuKey] || 'ShieldCheck'] || ShieldCheck;
  const groupColCls = groups.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3';

  return (
    <div className="rounded-2xl bg-white shadow-[0_16px_50px_rgba(0,0,0,0.08)] border border-zinc-100 overflow-hidden">
      <div className="grid grid-cols-12 gap-8 p-8">
        {/* LEFT — featured visual + tagline */}
        <div className="col-span-4">
          <div
            className="relative rounded-2xl overflow-hidden h-[260px] flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #fff8e7 0%, #fef3c7 50%, #fde68a 100%)' }}
          >
            <div
              className="absolute inset-0 opacity-25"
              style={{
                background:
                  'radial-gradient(circle at 30% 30%, rgba(245,166,35,0.55) 0%, transparent 55%), radial-gradient(circle at 75% 70%, rgba(255,140,26,0.35) 0%, transparent 55%)',
              }}
            />
            <LeadIcon className="relative w-28 h-28 text-[#8a6710]" strokeWidth={1.4} />
          </div>
          <h4 className="text-[20px] font-extrabold text-gray-900 mt-5 mb-1.5 leading-tight">{f.title}</h4>
          <p className="text-[13px] text-gray-500 leading-6 mb-3">{f.body}</p>
          <SmartLink
            to={f.ctaHref || '/systems/solutions'}
            onClick={() => {
              ping('featured-cta', f.cta);
              onNavigate && onNavigate();
            }}
            testId={`systems-nav-featured-cta-${(f.cta || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="inline-flex items-center gap-1 text-[14px] font-semibold text-[#c9a227] hover:underline"
          >
            {f.cta || 'Learn more'} <ArrowRight className="w-4 h-4" />
          </SmartLink>
        </div>

        {/* RIGHT — text-link columns, no icons */}
        <div className={`col-span-8 grid ${groupColCls} gap-x-8 gap-y-6`}>
          {groups.map((g) => (
            <div key={g.heading}>
              <span className="block text-[11px] font-bold tracking-[0.22em] text-gray-400 uppercase mb-4">
                {g.heading}
              </span>
              <ul className="space-y-3">
                {g.items.map((it) => (
                  <li key={it.label}>
                    <SmartLink
                      to={it.href}
                      onClick={() => {
                        ping(`item:${it.href}`, it.label);
                        onNavigate && onNavigate();
                      }}
                      testId={`systems-nav-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="text-[15px] text-gray-800 hover:text-[#c9a227] transition-colors"
                    >
                      {it.label}
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Footer divider — "Explore all X ›"  +  "Need help choosing? Contact a specialist" */}
      <div className="border-t border-gray-100 px-8 py-4 flex items-center justify-between flex-wrap gap-3">
        <SmartLink
          to={menu.href || '/systems/solutions'}
          onClick={() => {
            ping('explore-all', `Explore all ${menu.label}`);
            onNavigate && onNavigate();
          }}
          testId={`systems-nav-explore-all-${menuKey}`}
          className="inline-flex items-center gap-1 text-[14px] font-semibold text-[#c9a227] hover:underline"
        >
          Explore all {menu.label} <ArrowRight className="w-4 h-4" />
        </SmartLink>
        <div className="text-[13px] text-gray-500">
          Need help choosing?{' '}
          <Link
            to="/systems/contact"
            onClick={() => {
              ping('contact-specialist', 'Contact a specialist');
              onNavigate && onNavigate();
            }}
            data-testid={`systems-nav-specialist-${menuKey}`}
            className="font-semibold text-gray-900 hover:text-[#c9a227] transition-colors"
          >
            Contact a specialist
          </Link>
        </div>
      </div>
    </div>
  );
}

function NavItem({ label, icon: Icon, to = '#', hasMenu, menuKey, isOpen, testId, onOpen, onClose, onToggle }) {
  // Touch-device support: on tablets/phones "hover" doesn't exist, so
  // tapping the nav item must TOGGLE the mega-menu instead of navigating
  // straight to the parent route. Detected via pointer:coarse media query.
  const handleClick = (e) => {
    if (!hasMenu) return;
    const isCoarse = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) {
      if (!isOpen) {
        e.preventDefault();
        onToggle && onToggle(menuKey);
      }
    }
    trackNavClick({ slug: `systems:${menuKey || 'top'}:nav`, label });
  };
  return (
    <div
      className="relative"
      onMouseEnter={() => hasMenu && onOpen(menuKey)}
      onMouseLeave={() => hasMenu && onClose()}
    >
      <Link
        to={to}
        data-testid={testId}
        onClick={handleClick}
        aria-expanded={hasMenu ? isOpen : undefined}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        className={`flex items-center gap-1.5 px-4 py-2 text-[13.5px] font-semibold transition-colors ${
          isOpen ? 'text-zinc-900' : 'text-zinc-700 hover:text-zinc-900'
        }`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span>{label}</span>
        {hasMenu && (
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180 text-zinc-900' : 'text-zinc-400'}`}
            strokeWidth={2.5}
          />
        )}
      </Link>
    </div>
  );
}

export default function SystemsHeader() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const closeTimer = React.useRef(null);
  const { t } = useI18n();
  const nav = t.systems_nav;
  const hdr = t.header;
  const labelFor = (key, fallback) => nav[key] || fallback;
  const closeMobile = () => setMobileOpen(false);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchValue.trim();
    trackNavClick({ slug: 'systems:search:submit', label: q || 'empty' });
    navigate(q ? `/systems/solutions?q=${encodeURIComponent(q)}` : '/systems/solutions');
    setMobileOpen(false);
  };

  const openMenu = (key) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenKey(key);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // 350ms (was 150) — small cursor wobbles between nav item and panel
    // no longer dismiss the menu prematurely. Same fix we applied to
    // ServicesHeader so all 3 division headers feel identical.
    closeTimer.current = setTimeout(() => setOpenKey(null), 350);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const closeNow = () => {
    cancelClose();
    setOpenKey(null);
  };
  // Tap-toggle for coarse-pointer (touch) devices.
  const toggleMenu = (key) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenKey((prev) => (prev === key ? null : key));
  };

  // Outside-click close — essential for touch where there's no
  // mouseleave to dismiss the panel.
  React.useEffect(() => {
    if (!openKey) return undefined;
    const onDocClick = (e) => {
      const nav = e.target.closest && e.target.closest('[data-mega-nav="systems"]');
      if (!nav) closeNow();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openMenuObj = openKey ? SYSTEMS_MENU[openKey] : null;

  return (
    <header className="sticky top-0 z-50 w-full shadow-sm">
      {/* Top utility bar — kept for region / currency / language / search (our value-add over the
          reference site). Stays light so the selectors read well. */}
      <div className="bg-white border-b border-zinc-100">
        <div className="zh-container flex items-center justify-between gap-3 py-2 flex-wrap">
          {/* Left: persistent "back to main site" link so visitors who landed
              on /systems can always return to the cross-division home. */}
          <Link
            to="/"
            data-testid="systems-back-to-main"
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-500 hover:text-[#c9a227] transition-colors"
            title="Back to ZHELTA main site"
          >
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            ZHELTA
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <RegionSelector />
            <CurrencySelector />
            <LanguageSelector />
            <form
              onSubmit={handleSearch}
              role="search"
              data-testid="systems-search-form"
              className="relative flex items-center w-full sm:w-auto"
            >
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={hdr.search_placeholder}
                data-testid="systems-search-input"
              aria-label="Search ZHELTA Systems"
              className="w-full sm:w-56 md:w-72 pl-4 pr-10 py-1.5 rounded-full border border-amber-200/80 bg-white focus:outline-none focus:border-amber-400 text-sm transition-colors"
            />
            <button
              type="submit"
              aria-label="Search"
              data-testid="systems-search-submit"
              className="absolute right-2 p-1 text-gray-500 hover:text-[#c9a227] transition-colors"
            >
              <Search className="w-4 h-4" />
            </button>
          </form>
          </div>
        </div>
      </div>

      {/* Main nav bar — DARK, matching zhelta.com/systems */}
      <div className="bg-white text-zinc-900 border-b border-zinc-200 relative" data-mega-nav="systems">
        <div className="zh-container flex items-center h-[78px] gap-6">
          {/* Logo (left) — ZHELTA wordmark + golden "SYSTEMS" eyebrow
           *   Matches ServicesHeader + AcademyHeader so all three division
           *   headers share one identity system.
           */}
          <Link to="/systems" data-testid="systems-nav-logo" className="flex items-center gap-2.5 shrink-0">
            <ZheltaLogo size={40} />
            <div className="leading-none">
              <div className="text-[22px] font-extrabold tracking-[0.18em] text-zinc-900">ZHELTA</div>
              <div className="text-[9px] font-semibold tracking-[0.3em] text-[#f5a623] mt-0.5">SYSTEMS</div>
            </div>
          </Link>

          {/* Desktop nav — CENTERED */}
          <nav className="hidden lg:flex flex-1 items-center justify-center" onMouseLeave={scheduleClose}>
            <NavItem label={nav.home} icon={Home} to="/systems" testId="systems-nav-home" menuKey="home" isOpen={false} onOpen={closeNow} onClose={scheduleClose} />
            <NavItem label={nav.solutions} to={SYSTEMS_MENU.solutions.href} hasMenu menuKey="solutions" isOpen={openKey === 'solutions'} testId="systems-nav-solutions" onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
            <NavItem label={nav.pricing}   to={SYSTEMS_MENU.pricing.href}   hasMenu menuKey="pricing"   isOpen={openKey === 'pricing'}   testId="systems-nav-pricing"   onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
            <NavItem label={nav.resources} to={SYSTEMS_MENU.resources.href} hasMenu menuKey="resources" isOpen={openKey === 'resources'} testId="systems-nav-resources" onOpen={openMenu} onClose={scheduleClose} onToggle={toggleMenu} />
          </nav>

          {/* Filler so CTAs stay right-aligned on mobile (when nav is hidden) */}
          <div className="lg:hidden flex-1" />

          {/* CTAs (right) — Switch-division pill removed; the horizontal
              DivisionStripBar directly below this header already surfaces
              the other divisions, so the dropdown was redundant. */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <a
              href="https://portal.zhelta.com/p/scheduler/KfMLXSkC6pkS4utFn"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="systems-nav-book-demo"
              onClick={() => trackNavClick({ slug: 'systems:cta:book-demo', label: nav.book_demo })}
              className="px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase border-2 border-zinc-300 text-zinc-900 hover:bg-[#f5a623] hover:text-[#0e0e10] hover:border-[#f5a623] transition-colors"
            >
              {nav.book_demo}
            </a>
            <Link
              to="/systems/get-started"
              data-testid="systems-nav-get-started"
              onClick={() => trackNavClick({ slug: 'systems:cta:get-started', label: nav.get_started })}
              className="px-5 py-2.5 rounded-full text-[12px] font-semibold tracking-wider uppercase text-zinc-900 hover:brightness-95 transition-all"
              style={{
                background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
                boxShadow: '0 10px 20px -8px rgba(245, 140, 25, 0.5)',
              }}
            >
              {nav.get_started}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-zinc-900"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="systems-nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Single mega-menu panel, viewport-centered under the dark bar regardless of which item is hovered */}
        {openMenuObj && (
          <div
            className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(1240px,96vw)] z-[80]"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            data-testid={`systems-nav-mega-${openKey}`}
          >
            <MegaMenu menuKey={openKey} menu={openMenuObj} onNavigate={closeNow} />
          </div>
        )}
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-black/20 bg-white text-zinc-900 border-b border-zinc-200 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="zh-container py-3 flex flex-col">
            <Link to="/systems" onClick={closeMobile} data-testid="systems-nav-mobile-home" className="flex items-center gap-2 px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-100 rounded-md">
              <Home className="w-4 h-4" /> {nav.home}
            </Link>
            {Object.entries(SYSTEMS_MENU).filter(([, m]) => m).map(([key, m]) => (
              <div key={key}>
                <button
                  onClick={() => setExpanded(expanded === key ? null : key)}
                  className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-zinc-900 hover:bg-zinc-100 rounded-md"
                  data-testid={`systems-nav-mobile-toggle-${key}`}
                >
                  <span>{labelFor(key, m.label)}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${expanded === key ? 'rotate-180' : ''}`} />
                </button>
                {expanded === key && (
                  <div className="pl-5 pb-2">
                    {m.groups.map((g) => (
                      <div key={g.heading} className="mb-3">
                        <div className="text-[10px] font-bold tracking-[0.18em] text-zinc-500 mb-1.5 mt-2">{g.heading}</div>
                        <ul className="space-y-1.5">
                          {g.items.map((it) => {
                            const Icon = ICONS[it.icon] || MessageCircle;
                            return (
                              <li key={it.label}>
                                <SmartLink
                                  to={it.href}
                                  onClick={() => {
                                    trackNavClick({ slug: `systems:${key}:item:${it.href}`, label: it.label });
                                    closeMobile();
                                  }}
                                  className="flex items-center gap-2 text-[13px] text-zinc-700 py-1 hover:text-[#f5a623]"
                                >
                                  <Icon className="w-3.5 h-3.5 text-zinc-500" />
                                  {it.label}
                                </SmartLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                    {m.footer && m.footer.length > 0 && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 pb-2">
                        {m.footer.map((link) => (
                          <SmartLink
                            key={link.label}
                            to={link.href}
                            onClick={closeMobile}
                            className="text-[12px] text-zinc-500 hover:text-[#f5a623] font-medium"
                          >
                            {link.label}
                          </SmartLink>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center gap-3 px-3 pt-4 pb-3">
              <a
                href="https://portal.zhelta.com/p/scheduler/KfMLXSkC6pkS4utFn"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobile}
                data-testid="systems-nav-mobile-book-demo"
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-zinc-900 border-2 border-zinc-300"
              >
                {nav.book_demo}
              </a>
              <Link
                to="/systems/get-started"
                onClick={closeMobile}
                className="flex-1 text-center px-4 py-2.5 rounded-full text-[13px] font-semibold uppercase text-white"
                style={{ background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)' }}
              >
                {nav.get_started}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
